import {
  Button,
  Form,
  Input,
  Radio,
  Space,
  Tabs,
  Typography,
  message,
} from 'antd';
import axios from 'axios';
import { RecordFieldWrapper } from 'components/RecordFields/style';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { FETCH_ERROR } from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { useEffect, useState } from 'react';
import { generateQueryParams } from 'utils/generateQueryParams';
import { requiredRule } from 'utils/forms/validationRules';
import { useSearchParams } from 'react-router-dom';
import { BCCRefundService } from 'services/BCCRefundService';
import dayjs from 'dayjs';
import {
  doneRecordFields,
  recordFields,
  returnRecordFields,
  returnReplyTableNextDrawer,
  returnWorkedOutFields,
  statusText,
} from './mock';
import BccPaymentetsRefundTable from './BccPaymentsRefundTable/BccPaymentetsRefundTable';
import BccPaymentsRefundTableForm from './BccPaymentsRefundTableForm/BccPaymentsRefundTableForm';

const PAGE_TITLE = 'Возврат налогов из БЦК';

function BccPaymentsRefund() {
  useDocumentTitle(PAGE_TITLE);
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const [params] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabActive, setTabActive] = useState('return');
  const [radioValue, setRadioValue] = useState('229');
  const [inputValue, setInputValue] = useState();
  const [isNew, setIsNew] = useState(false);
  const [form] = Form.useForm();
  const tabDrawer = useDrawers();
  const returnDrawer = useDrawers();
  const workedOutDrawer = useDrawers();
  const replyTableDrawer = useDrawers();
  const replyTableNextDrawer = useDrawers();
  const { filters, handleFilters } = useFilters({
    f_date:
      params.get('f_date') ?? dayjs().subtract(9, 'days').format('YYYY-MM-DD'),
    t_date: params.get('t_date') ?? dayjs().format('YYYY-MM-DD'),
  });
  const { f_date, t_date } = filters;

  useEffect(() => {
    setIsNew(tabDrawer.state?.activeRecord?.status === 'new');
    fetchRefundPayments(f_date, t_date);
    handleFilters();
  }, [tabDrawer.state.drawerVisible]);

  useEffect(() => {
    form.resetFields();
  }, [replyTableDrawer.state.drawerVisible]);

  const fetchRefundPayments = async (startDate, endDate) => {
    const params = generateQueryParams({
      f_date: startDate,
      t_date: endDate,
    });

    setLoading(true);

    try {
      const res = await axios.get('payments/v1/report/refund/', {
        params,
      });

      if (res.status === 200) {
        setPayments(res.data);
        setCount(res.data.length);
      }
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = (range) => {
    const [start, end] = range;
    handleFilters({ f_date: start, t_date: end });
    fetchRefundPayments(start, end);
  };

  const handleFormFinish = async (value, status, type, extRefNo, drawer) => {
    const payload = {
      ...value,
      status,
      type,
    };

    try {
      const res = await BCCRefundService.postBccRefund(payload, extRefNo);

      if (res) {
        drawer.openDrawer();
      }
    } catch (error) {
      message.error('Не удалось выполнить запрос на сервер');
    }
  };

  const handleFormTableFinish = async (values, extRefNo, drawer) => {
    const register = values.fm.map((item, i) => {
      return {
        amount: Number(values.amount[i]),
        iin: values.iin[i],
        fm: item,
        nm: values.nm[i],
        ft: values.ft[i],
        dt: values.dt[i],
        period: values.period[i],
        worker_id: tabDrawer.state.activeRecord.reportRows[i].worker_id ?? null,
      };
    });

    const payload = {
      register,
      status: 'refund',
      type: 'resend',
    };

    try {
      const res = await BCCRefundService.postBccRefund(payload, extRefNo);

      if (res) {
        drawer.openDrawer();
      }
    } catch (error) {
      message.error('Не удалось выполнить запрос на сервер');
    }
  };

  return (
    <>
      {/* Drawer с табами */}
      <RecordInfoDrawer
        record={{
          ...tabDrawer.state.activeRecord,
          status: statusText[tabDrawer.state.activeRecord?.status],
        }}
        recordFields={isNew ? recordFields : doneRecordFields}
        drawerTitle={tabDrawer.state.activeRecord?.company_name}
        isVisible={tabDrawer.state.drawerVisible}
        onClose={tabDrawer.closeDrawer}
        width="70%"
        editable={false}
      >
        <RecordFieldWrapper>
          <div className="record__key">&nbsp;</div>

          <div className="record__value">
            {isNew ? (
              <Tabs
                defaultActiveKey="1"
                onChange={(i) => {
                  setTabActive(i);
                }}
              >
                <Tabs.TabPane tab="Возврат" key="return" />
                <Tabs.TabPane tab="Отработано" key="worked out" />
                <Tabs.TabPane tab="Переотправить" key="reply" />
              </Tabs>
            ) : null}
          </div>
        </RecordFieldWrapper>

        {tabActive === 'return' && isNew ? (
          <>
            <RecordFieldWrapper>
              <div className="record__key">&nbsp;</div>
              <div
                className="record__value"
                style={{ paddingBottom: '16px', width: '400px' }}
              >
                <div style={{ width: '400px' }}>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: '13px' }}
                  >
                    Возврат денег клиенту осуществляется бухгалтерией вручную.
                    Возвратные деньги будут переведены на банковский счет **229
                  </Typography.Text>
                </div>
              </div>
            </RecordFieldWrapper>

            <Form
              onFinish={(value) =>
                handleFormFinish(
                  value,
                  'refund_client',
                  'refund',
                  tabDrawer?.state.activeRecord.extRefNo,
                  returnDrawer
                )
              }
            >
              <RecordFieldWrapper style={{ paddingBottom: '16px' }}>
                <div className="record__key">Комментарий:</div>
                <div className="record__value">
                  <Form.Item
                    rules={[requiredRule]}
                    name="comment"
                    style={{ margin: 0 }}
                  >
                    <Input
                      value={inputValue}
                      onChange={(e) => {
                        setInputValue(e.target.value);
                      }}
                      size="large"
                      placeholder="Напишите текст"
                    />
                  </Form.Item>
                </div>
              </RecordFieldWrapper>

              <RecordFieldWrapper>
                <div className="record__key">&nbsp;</div>
                <div className="record__value">
                  <Form.Item>
                    <Button type="primary" htmlType="submit">
                      Отправить
                    </Button>
                  </Form.Item>
                </div>
              </RecordFieldWrapper>
            </Form>
          </>
        ) : null}

        {tabActive === 'worked out' && isNew ? (
          <>
            <RecordFieldWrapper>
              <div className="record__key">&nbsp;</div>
              <div className="record__value" style={{ paddingBottom: '16px' }}>
                <div style={{ width: '400px' }}>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: '13px', lineHeight: '20px' }}
                  >
                    Опция для отработки вручную. Выберите банковский счет куда
                    будут переведены деньги. <br /> Внимание! Деньги
                    отправленные на банковский счет **567 будут признаны как
                    доход для компании.
                  </Typography.Text>
                </div>
              </div>
            </RecordFieldWrapper>

            <Form
              onFinish={(value) => {
                handleFormFinish(
                  value,
                  'income',
                  'income',
                  tabDrawer?.state.activeRecord.extRefNo,
                  workedOutDrawer
                );
              }}
            >
              <RecordFieldWrapper style={{ paddingBottom: '16px' }}>
                <div className="record__key">Комментарий:</div>
                <div className="record__value">
                  <Form.Item name="comment" rules={[requiredRule]}>
                    <Input
                      value={inputValue}
                      onChange={(e) => {
                        setInputValue(e.target.value);
                      }}
                      size="large"
                      placeholder="Напишите текст"
                    />
                  </Form.Item>
                </div>
              </RecordFieldWrapper>

              <Form.Item initialValue={229} name="account">
                <RecordFieldWrapper style={{ paddingBottom: '16px' }}>
                  <div className="record__key" style={{ alignSelf: 'start' }}>
                    Банк. счет:
                  </div>

                  <div className="record__value">
                    <Radio.Group
                      onChange={(e) => {
                        setRadioValue(e.target.value);
                      }}
                      value={radioValue}
                    >
                      <Space direction="vertical">
                        <Radio value="229">**229</Radio>
                        <Radio value="567">**567 </Radio>
                      </Space>
                    </Radio.Group>
                  </div>
                </RecordFieldWrapper>
              </Form.Item>

              <RecordFieldWrapper>
                <div className="record__key">&nbsp;</div>
                <div className="record__value">
                  <Form.Item>
                    <Button type="primary" htmlType="submit">
                      Отправить
                    </Button>
                  </Form.Item>
                </div>
              </RecordFieldWrapper>
            </Form>
          </>
        ) : null}

        {tabActive === 'reply' && isNew ? (
          <>
            <RecordFieldWrapper>
              <div className="record__key">&nbsp;</div>
              <div className="record__value" style={{ paddingBottom: '16px' }}>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: '13px', lineHeight: '20px' }}
                >
                  Опция для исправления данных и переотправки в БЦК
                </Typography.Text>
              </div>
            </RecordFieldWrapper>

            <RecordFieldWrapper>
              <div className="record__key">&nbsp;</div>
              <div className="record__value">
                <Button
                  type="primary"
                  onClick={() => replyTableDrawer.openDrawer()}
                >
                  Перейти к форме
                </Button>
              </div>
            </RecordFieldWrapper>
          </>
        ) : null}
      </RecordInfoDrawer>

      {/* Drawer после нажатия отправить в табе возврат */}
      <RecordInfoDrawer
        record={{
          ...tabDrawer.state.activeRecord,
          comment: inputValue,
          status: 'Возвращено клиенту',
        }}
        recordFields={returnRecordFields}
        drawerTitle={tabDrawer.state.activeRecord?.company_name}
        isVisible={returnDrawer.state.drawerVisible}
        onClose={returnDrawer.closeDrawer}
        width="70%"
        editable={false}
      />

      {/* Drawer после нажатия отправить в табе отработано */}
      <RecordInfoDrawer
        record={{
          ...tabDrawer.state.activeRecord,
          comment: inputValue,
          bank_acc: `**${radioValue}`,
          status: 'Отработано',
        }}
        recordFields={returnWorkedOutFields}
        drawerTitle={tabDrawer.state.activeRecord?.company_name}
        isVisible={workedOutDrawer.state.drawerVisible}
        onClose={workedOutDrawer.closeDrawer}
        width="70%"
        bodyStyle="100%"
        editable={false}
      />

      {/* Drawer после нажатия Перейти к форме в табе Переотправить */}
      <RecordInfoDrawer
        record={{
          ...tabDrawer.state.activeRecord,
          status: statusText[tabDrawer.state.activeRecord?.status],
        }}
        recordFields={recordFields}
        drawerTitle="Переотправить в БЦК"
        isVisible={replyTableDrawer.state.drawerVisible}
        onClose={replyTableDrawer.closeDrawer}
        width="70%"
        bodyStyle="100%"
        editable={false}
      >
        <Form
          form={form}
          name="basic"
          onFinish={(values) =>
            handleFormTableFinish(
              values,
              tabDrawer?.state.activeRecord.extRefNo,
              replyTableNextDrawer
            )
          }
        >
          {tabDrawer.state.activeRecord !== null ? (
            <BccPaymentsRefundTableForm loading={loading} drawer={tabDrawer} />
          ) : null}

          <RecordFieldWrapper>
            <div className="record__key">&nbsp;</div>
            <div className="record__value">
              <Form.Item style={{ paddingTop: '8px' }}>
                <Button type="primary" htmlType="submit">
                  Отправить
                </Button>
              </Form.Item>
            </div>
          </RecordFieldWrapper>
        </Form>
      </RecordInfoDrawer>

      {/* Drawer после ОТПРАВКИ формы */}
      <RecordInfoDrawer
        record={{
          ...tabDrawer.state.activeRecord,
          status: 'Переотправлен в БЦК',
        }}
        recordFields={returnReplyTableNextDrawer}
        drawerTitle={tabDrawer.state.activeRecord?.company_name}
        isVisible={replyTableNextDrawer.state.drawerVisible}
        onClose={replyTableNextDrawer.closeDrawer}
        width="70%"
        bodyStyle="100%"
        editable={false}
      />

      {/* Основная Таблица */}
      <BccPaymentetsRefundTable
        payments={payments}
        handleDateFilter={handleDateFilter}
        f_date={f_date}
        t_date={t_date}
        loading={loading}
        openDrawer={tabDrawer.openDrawer}
      />
    </>
  );
}

export { BccPaymentsRefund };
